import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { categories } from "@/mocks/categories";
import { platforms } from "@/mocks/platforms";
import { articles } from "@/mocks/articles";
import { faqs } from "@/mocks/faqs";

const stats = [
  { value: "6+", label: "官方正版平台" },
  { value: "5000+", label: "正版作品入口" },
  { value: "6", label: "核心漫画分类" },
  { value: "100%", label: "正版授权收录" },
];

export default function Home() {
  const location = useLocation();
  const [query, setQuery] = useState("");
  const [openFaq, setOpenFaq] = useState<number>(0);

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.replace("#", ""));
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  }, [location.hash]);

  const filteredArticles = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return articles;
    return articles.filter(
      (a) => a.title.toLowerCase().includes(q) || a.category.toLowerCase().includes(q)
    );
  }, [query]);

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Navbar variant="hero" />

      {/* ===== Hero ===== */}
      <section className="relative flex w-full items-center justify-center overflow-hidden bg-foreground-950">
        <img
          src="https://readdy.ai/api/search-image?query=Stylized%20Korean%20webtoon%20collage%20illustration%20with%20floating%20comic%20pages%2C%20speech%20bubbles%20and%20soft%20stars%2C%20deep%20warm%20brown%20and%20cream%20toned%20background%2C%20dark%20moody%20editorial%20magazine%20style%2C%20clean%20manga%20lineart%2C%20atmospheric%20lighting%2C%20high%20detail%20digital%20art&width=1600&height=900&seq=hero-hanman-2026&orientation=landscape"
          alt="正版韩漫大全官方入口与正版渠道聚合首屏"
          className="absolute inset-0 h-full w-full object-cover object-top opacity-90"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/40 to-black/60"></div>

        <div className="relative z-10 w-full max-w-4xl px-4 pb-20 pt-32 text-center md:px-6 md:pb-28 md:pt-40">
          <span className="inline-flex items-center gap-2 rounded-full border border-background-50/30 bg-background-50/10 px-4 py-1.5 text-xs text-background-100 backdrop-blur-sm">
            <i className="ri-verified-badge-line text-secondary-300"></i>
            正版韩漫大全正版渠道聚合入口
          </span>
          <h1 className="mt-6 font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
            正版韩漫大全<span className="mx-2 inline-block text-primary-400">·</span>官方入口与正版渠道聚合
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-sm leading-relaxed text-background-100/90 md:text-base">
            集中整理正版韩漫大全官方入口、正版授权与独家内容，直达官方分类，快速找到想看的韩漫，
            避开盗版站，追更更省心。
          </p>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              document.getElementById("featured")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="mx-auto mt-8 flex max-w-xl flex-col gap-2 sm:flex-row"
            role="search"
            aria-label="搜索韩漫内容"
          >
            <div className="relative flex-1">
              <i className="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-base text-foreground-400"></i>
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="输入作品名或题材，如：恋爱、重生、奇幻…"
                className="w-full rounded-md bg-background-50 py-3 pl-11 pr-4 text-sm text-foreground-900 outline-none ring-0 placeholder:text-foreground-400 focus:ring-2 focus:ring-primary-400"
              />
            </div>
            <button
              type="submit"
              className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              立即搜索
            </button>
          </form>

          <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-4">
            {stats.map((s) => (
              <div key={s.label} className="rounded-lg bg-background-50/10 p-3 backdrop-blur-sm">
                <p className="font-comic text-xl text-secondary-300 md:text-2xl">{s.value}</p>
                <p className="mt-1 text-xs text-background-100/80">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Official platforms ===== */}
      <section id="official" className="mx-auto w-full max-w-7xl scroll-mt-20 px-4 py-14 md:px-6 md:py-20">
        <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
          <div>
            <span className="text-xs font-medium uppercase tracking-widest text-primary-600">官方入口</span>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-900 md:text-3xl">官方正版韩漫大全渠道</h2>
            <p className="mt-2 max-w-xl text-sm text-foreground-600">
              以下为正版韩漫大全收录的官方授权平台，均公开标注授权关系，认准入口即可安心追更。
            </p>
          </div>
          <span className="hidden items-center gap-2 rounded-full bg-accent-100 px-4 py-1.5 text-xs font-medium text-accent-900 md:inline-flex">
            <i className="ri-shield-check-line"></i> 仅收录正版授权
          </span>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {platforms.map((p) => (
            <article
              key={p.id}
              className="group rounded-lg border border-background-200 bg-background-50 p-5 transition-all hover:border-primary-300 hover:bg-primary-50/40"
            >
              <div className="flex items-start justify-between">
                <span className={`flex h-11 w-11 items-center justify-center rounded-md ${p.color} text-background-50`}>
                  <i className={`text-xl ${p.icon}`}></i>
                </span>
                <span className="rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-900">
                  {p.badge}
                </span>
              </div>
              <h3 className="mt-4 text-base font-semibold text-foreground-900">{p.name}</h3>
              <p className="mt-0.5 text-xs text-foreground-500">{p.subtitle}</p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">{p.desc}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {p.tags.map((t) => (
                  <span key={t} className="rounded-md bg-background-100 px-2 py-0.5 text-xs text-foreground-600">
                    {t}
                  </span>
                ))}
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ===== Categories ===== */}
      <section id="categories" className="scroll-mt-20 bg-background-100 py-14 md:py-20">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
          <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
            <div>
              <span className="text-xs font-medium uppercase tracking-widest text-primary-600">漫画分类</span>
              <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-900 md:text-3xl">按兴趣找到你的韩漫</h2>
              <p className="mt-2 max-w-xl text-sm text-foreground-600">
                六大核心分类，正版韩漫大全已为你整理好各类型的官方正版作品入口。
              </p>
            </div>
            <Link
              to="/articles"
              className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              浏览全部内容 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3">
            {categories.map((c) => (
              <Link
                key={c.id}
                to={`/category/${c.slug}`}
                className="group relative overflow-hidden rounded-lg"
                aria-label={`浏览${c.name}分类`}
              >
                <img
                  src={c.image}
                  alt={`${c.name}韩漫分类封面`}
                  className="aspect-[3/4] w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent"></div>
                <div className="absolute inset-x-0 bottom-0 p-4">
                  <div className="flex items-center gap-2 text-background-50">
                    <span className={`flex h-8 w-8 items-center justify-center rounded-md ${c.color} text-sm`}>
                      <i className={c.icon}></i>
                    </span>
                    <div>
                      <h3 className="font-heading text-base font-bold">{c.name}</h3>
                      <p className="text-xs text-background-100/80">{c.tagline}</p>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between border-t border-background-50/20 pt-3">
                    <span className="text-xs text-background-100/80">{c.count}+ 部正版作品</span>
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-background-50/15 text-background-50 transition-colors group-hover:bg-primary-500">
                      <i className="ri-arrow-right-line text-sm"></i>
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Featured / search results ===== */}
      <section id="featured" className="mx-auto w-full max-w-7xl scroll-mt-20 px-4 py-14 md:px-6 md:py-20">
        <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
          <div>
            <span className="text-xs font-medium uppercase tracking-widest text-primary-600">精选推荐</span>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-900 md:text-3xl">
              {query ? `“${query}” 的搜索结果` : "热门追更指南与推荐"}
            </h2>
            <p className="mt-2 max-w-xl text-sm text-foreground-600">
              {query
                ? `共找到 ${filteredArticles.length} 篇相关内容。`
                : "收录追更指南、精选推荐与版权科普，帮你快速上手正版韩漫。"}
            </p>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredArticles.map((a) => (
            <Link
              key={a.id}
              to={`/article/${a.slug}`}
              className="group overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-all hover:-translate-y-1 hover:border-primary-300"
            >
              <div className="relative overflow-hidden">
                <img
                  src={a.cover}
                  alt={`${a.title}封面`}
                  className="aspect-[3/2] w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute left-3 top-3 rounded-full bg-primary-500 px-2.5 py-1 text-xs font-medium text-background-50">
                  {a.tag}
                </span>
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 text-xs text-foreground-500">
                  <span className="text-primary-600">{a.category}</span>
                  <span>·</span>
                  <span>{a.date}</span>
                  <span>·</span>
                  <span>{a.readTime}</span>
                </div>
                <h3 className="mt-3 font-heading text-base font-bold leading-snug text-foreground-900 group-hover:text-primary-600">
                  {a.title}
                </h3>
                <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{a.summary}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary-600">
                  阅读全文 <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
                </span>
              </div>
            </Link>
          ))}
        </div>

        {filteredArticles.length === 0 && (
          <div className="mt-10 rounded-lg border border-dashed border-background-300 bg-background-100 p-10 text-center">
            <i className="ri-search-eye-line text-4xl text-foreground-400"></i>
            <p className="mt-3 text-sm text-foreground-600">没有找到相关结果，换个关键词试试吧。</p>
          </div>
        )}
      </section>

      {/* ===== Why official ===== */}
      <section className="bg-primary-50/50 py-14 md:py-20">
        <div className="mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-10 px-4 md:grid-cols-2 md:px-6">
          <div className="relative">
            <img
              src="https://readdy.ai/api/search-image?query=Stylized%20Korean%20webtoon%20illustration%20of%20an%20artist%20drawing%20comic%20panels%20at%20a%20desk%20with%20warm%20lamp%20light%2C%20cream%20and%20coral%20palette%2C%20cozy%20creative%20studio%20atmosphere%2C%20clean%20manga%20lineart%2C%20high%20detail%20digital%20art&width=1000&height=820&seq=why-official-2026&orientation=portrait"
              alt="尊重创作者的韩漫版权插画"
              className="aspect-[5/4] w-full rounded-lg object-cover object-top"
            />
            <div className="absolute -bottom-4 -right-4 hidden rounded-lg bg-accent-500 px-5 py-4 text-background-50 md:block">
              <p className="font-comic text-2xl">100%</p>
              <p className="text-xs">正版授权收录</p>
            </div>
          </div>
          <div>
            <span className="text-xs font-medium uppercase tracking-widest text-primary-600">为什么看正版</span>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-900 md:text-3xl">
              选择正版，是每一位读者的温柔
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              一部韩漫从分镜到上色，倾注了作者数月乃至数年的心血。选择正版韩漫大全收录的官方渠道，
              你获得的不只是高清画质与稳定更新，更是对创作的尊重。
            </p>
            <ul className="mt-6 space-y-4">
              {[
                { icon: "ri-file-shield-2-line", title: "画质与翻译有保障", desc: "官方渠道画质清晰、翻译流畅，阅读体验更佳。" },
                { icon: "ri-money-cny-circle-line", title: "支持创作者创作", desc: "付费收益按比例分成，让作者持续产出好作品。" },
                { icon: "ri-shield-check-line", title: "远离安全风险", desc: "盗版站常夹带广告与恶意程序，正版渠道更安全。" },
              ].map((item) => (
                <li key={item.title} className="flex gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-500 text-background-50">
                    <i className={`text-lg ${item.icon}`}></i>
                  </span>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900">{item.title}</h3>
                    <p className="mt-0.5 text-sm text-foreground-600">{item.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section id="faq" className="mx-auto w-full max-w-4xl scroll-mt-20 px-4 py-14 md:px-6 md:py-20">
        <div className="text-center">
          <span className="text-xs font-medium uppercase tracking-widest text-primary-600">常见问题</span>
          <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-900 md:text-3xl">关于正版韩漫的常见疑问</h2>
          <p className="mx-auto mt-2 max-w-xl text-sm text-foreground-600">看正版韩漫会遇到的问题，这里都有答案。</p>
        </div>

        <div className="mt-8 space-y-3">
          {faqs.slice(0, 4).map((f, i) => (
            <div key={f.id} className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
              <button
                type="button"
                onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                aria-expanded={openFaq === i}
              >
                <span className="text-sm font-medium text-foreground-900">{f.question}</span>
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-background-100 text-foreground-600">
                  <i className={`transition-transform ${openFaq === i ? "rotate-45 ri-add-line" : "ri-add-line"}`}></i>
                </span>
              </button>
              {openFaq === i && (
                <div className="border-t border-background-200 px-5 py-4">
                  <p className="text-sm leading-relaxed text-foreground-600">{f.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-8 text-center">
          <Link
            to="/faq"
            className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-primary-200 bg-primary-50 px-5 py-2.5 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-100"
          >
            查看全部常见问题 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="bg-foreground-950">
        <div className="mx-auto flex w-full max-w-7xl flex-col items-center justify-between gap-6 px-4 py-12 md:flex-row md:px-6 md:py-16">
          <div className="text-center md:text-left">
            <h2 className="font-heading text-xl font-bold text-background-50 md:text-2xl">想进一步了解正版韩漫大全？</h2>
            <p className="mt-2 max-w-xl text-sm text-background-100/80">
              欢迎查看我们的追更指南，或直接联系我们，获取更多正版韩漫资讯与帮助。
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Link
              to="/about"
              className="whitespace-nowrap rounded-md border border-background-50/30 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10"
            >
              关于我们
            </Link>
            <Link
              to="/contact"
              className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              联系我们
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}